import { IntlShape, defineMessages } from 'react-intl'
import * as yup from 'yup'

import { INewBusinessSiteValues } from 'features/BusinessSites/types'
import { schema as contactsSchema } from 'features/BusinessSites/components/ContactsFormCard/schema'
import { isEmpty } from 'lodash'
import { getDifference } from 'utils/object'

const nameMessages = defineMessages({
  required: {
    id: 'businessSites.new.name.required',
    defaultMessage: 'The site name is required'
  },
  max: {
    id: 'businessSites.new.name.max',
    defaultMessage: 'The site name must be at most {max} characters'
  }
})

const clientMessages = defineMessages({
  required: {
    id: 'businessSites.new.client.required',
    defaultMessage: 'The client is required'
  }
})

const messages = defineMessages({
  required: {
    id: 'businessSites.new.field.required',
    defaultMessage: 'This field is required'
  }
})

export const schema = (
  intl: IntlShape,
  initialValues: INewBusinessSiteValues
): yup.SchemaOf<INewBusinessSiteValues> => {
  return yup.object().shape({
    name: yup
      .string()
      .max(30, ({ max }) => intl.formatMessage(nameMessages.max, { max }))
      .required(intl.formatMessage(nameMessages.required)),
    client: yup.object().shape({
      id: yup.string().required(intl.formatMessage(clientMessages.required)),
      name: yup.string().required()
    }),
    city: yup.object().shape({
      id: yup.string(),
      name: yup.string(),
      stateCode: yup.string()
    }),
    kreditsSettings: yup.object().when('$kreditsSettings', {
      is: (value: INewBusinessSiteValues['kreditsSettings']) => {
        value.categories = value.categories.map((category) => ({
          ...category,
          amount: category.amount ?? '',
          name: category.name ?? ''
        }))
        const kreditsSettingsPayload = getDifference(value, initialValues.kreditsSettings)

        return !isEmpty(kreditsSettingsPayload)
      },
      then: yup
        .object()
        .shape({
          categories: yup.array().of(
            yup.object().shape({
              name: yup.string().required(intl.formatMessage(messages.required)),
              frequency: yup.object().shape({
                value: yup.string().required(intl.formatMessage(messages.required)),
                unit: yup.string().required(intl.formatMessage(messages.required))
              }),
              amount: yup.string().required(intl.formatMessage(messages.required))
            })
          )
        })
        .required(intl.formatMessage(messages.required)),
      otherwise: yup.object()
    }) as yup.SchemaOf<INewBusinessSiteValues['kreditsSettings']>,
    ...contactsSchema(intl).fields
  })
}
